<template>
  <div>
    <v-data-table
      :headers="activeHeaders"
      :items="activeTableRows"
      item-key="key"
      fixed-header
      disable-pagination
      hide-default-footer
      :disable-sort="isMobile || disableSort"
      :group-by="shouldGroupByBooking ? ['booking_id'] : []"
      class="charges-table"
    >
      <template v-if="summaryToggleEnabled" v-slot:footer>
        <div class="px-4 pt-4 d-flex align-center justify-end">
          <v-switch
            v-model="showProductSummary"
            color="secondary"
            dense
            hide-details
            class="ma-0 pa-0"
            label="Visa produktsammanfattning"
          ></v-switch>
        </div>
      </template>

      <template v-slot:no-data>
        <template v-if="loading">
          <v-simple-table class="skeleton-loader-table">
            <tr v-for="i in 2" :key="i">
              <td v-for="(header, x) in activeHeaders" :key="x" :class="{ 'text-left': header.align === 'start', 'text-right': header.align === 'end' }">
                <v-skeleton-loader
                  style=""
                  type="table-cell"
                  class=""
                ></v-skeleton-loader>
              </td>
            </tr>
          </v-simple-table>
        </template>
        <template v-else>
          <v-alert
            dense
            text
            color="info"
            class="ma-10"
          >
            Det finns inga avgifter att visa
          </v-alert>
        </template>
      </template>

      <template v-slot:[`group.header`]="{ group: bookingId, isOpen, toggle }">
        <td :colspan="activeHeaders.length" class="grey lighten-4 cursor-pointer" @click="toggle">
          <div class="d-flex full-width">
            <v-icon class="mr-3">{{ isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <div class="subtitle-1 font-weight-medium my-auto">{{ getBookingTitle(bookingId) }}</div>
            <template v-if="getBookingIsDeleted(bookingId)">
              <v-chip
                class="ml-2"
                small
                color="error"
                text-color="white"
              >
                Avbokad
              </v-chip>
            </template>
          </div>
        </td>
      </template>

      <template v-slot:[`item.move_left`]="{ item }">
        <div class="d-flex align-center justify-start">
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'left', quantity: 'all' })"
            fab
            small
            color="error"
            class="mr-2"
            elevation="3"
          >
            <v-icon>mdi-transfer-left</v-icon>
          </v-btn>
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'left', quantity: 'one' })"
            fab
            small
            color="error"
            class="mr-2"
            elevation="3"
          >
            <v-icon>coi-one-left</v-icon>
          </v-btn>
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'left', quantity: 'x' })"
            fab
            small
            color="error"
            elevation="3"
          >
            <v-icon>coi-x-left</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.name`]="{ item }">
        <div :class="{ 'pt-2': !!item.deliveryDateFormatted || !!item.note }">
          <v-chip
            :color="item.guiColor"
            dark
          >
            <v-icon v-if="item.isTallyCharge" class="mr-2">coi-access-card</v-icon>
            <v-icon v-else-if="item.isBookvisitCharge" class="mr-2">mdi-cloud-sync</v-icon>
            {{ item.name }}
          </v-chip>
        </div>
        <div v-show="!!item.deliveryDateFormatted || !!item.note" class="pb-2 pt-1 pl-3 text-caption">
          <span
            v-if="item.deliveryDateFormatted"
            class="delivery-date-clickable cursor-pointer secondary--text secondary-darken-text-on-hover"
            @click="openDatePicker(item)"
          >
            <v-icon x-small class="mr-1" color="secondary">mdi-calendar</v-icon>
            {{ item.deliveryDateFormatted }}
          </span>
          <span v-if="item.deliveryDateFormatted && item.note" class="mx-2">/</span>
          <span v-if="item.note">{{ item.note }}</span>
        </div>
      </template>

      <template v-slot:[`item.price`]="{ item }">
        <template v-if="item.discountType === null || item.originalPrice === 0">
          {{ item.priceFormatted }}
        </template>
        <template v-else-if="item.discountType === 'fixed'">
          <div>
            <span class="text-decoration-line-through">
              {{ item.originalPriceFormatted }}
            </span>
            <span class="red--text ml-2">
              {{ item.priceFormatted }}
            </span>
          </div>
          <div>
            <i>Rabatt: {{ item.fixedPriceDiscountValueFormatted }}</i>
          </div>
        </template>
        <template v-else-if="item.discountType === 'flat'">
          <div>
            <span class="text-decoration-line-through">
              {{ item.originalPriceFormatted }}
            </span>
            <span class="red--text ml-2">
              {{ item.priceFormatted }}
            </span>
          </div>
          <div>
            <i>Rabatt: {{ item.flatPriceDiscountValueFormatted }}</i>
          </div>
        </template>
        <template v-else-if="item.discountType === 'percent'">
          <div>
            <span class="text-decoration-line-through">
              {{ item.originalPriceFormatted }}
            </span>
            <span class="red--text ml-2">
              {{ item.priceFormatted }}
            </span>
          </div>
          <div>
            <i>Rabatt: {{ item.discountValue / 100 }}%</i>
          </div>
        </template>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <div class="d-flex align-center justify-end">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-icon v-show="item.status === 'paid'" color="success">mdi-cash-check</v-icon>
                <v-icon v-show="item.status === 'invoiced'" color="success">mdi-file-document-check</v-icon>
                <v-icon v-show="item.status === 'parked'" color="primary">mdi-cart-outline</v-icon>
              </div>
            </template>
            <div>{{ item.statusFormatted }}</div>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:[`item.totalFormatted`]="{ item }">
        <div class="text-right">{{ item.totalFormatted }}</div>
      </template>

      <template v-slot:[`item.paidOrInvoicedFormatted`]="{ item }">
        <div class="text-right">{{ item.paidOrInvoicedFormatted }}</div>
      </template>

      <template v-slot:[`item.inCartFormatted`]="{ item }">
        <div class="text-right">{{ item.inCartFormatted }}</div>
      </template>

      <template v-slot:[`item.move_right`]="{ item }">
        <div class="d-flex align-center justify-end">
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'right', quantity: 'x' })"
            fab
            small
            color="success"
            class="mr-2"
            elevation="3"
          >
            <v-icon>coi-x-right</v-icon>
          </v-btn>
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'right', quantity: 'one' })"
            fab
            small
            color="success"
            class="mr-2"
            elevation="3"
          >
            <v-icon>coi-one-right</v-icon>
          </v-btn>
          <v-btn
            @click="moveCharge({ chargeId: item.id, direction: 'right', quantity: 'all' })"
            fab
            small
            color="success"
            elevation="3"
          >
            <v-icon>mdi-transfer-right</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-if="showTotalRow && activeTableRows.length > 0" v-slot:[`body.append`]>
        <tr class="charges-table-summary-row">
          <td
            v-for="(header, index) in activeHeaders"
            :key="header.value"
            :class="{ 'text-left': index === 0, 'text-right': index !== 0 }"
          >
            <template v-if="index === 0">
              Total
            </template>
            <template v-else>
              {{ getSummaryCellValue(header) }}
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Date Picker Dialog -->
    <v-dialog v-model="datePickerDialog.show" max-width="380">
      <v-card class="pa-4">
        <date-picker
          v-model="datePickerDialog.deliveryDate"
          title="Leveransdatum"
          prompt-title="Välj leveransdatum"
          always-show-year
          :is-mobile="isMobile"
          :date-chips="deliveryDateSuggestions"
        ></date-picker>
        <div class="d-flex justify-end mt-4">
          <v-btn
            @click="saveDeliveryDate"
            color="success"
          >
            Spara
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.charges-table-summary-row td {
  background-color: #fafafa;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 500;
}
</style>

<script>
import BookingService from '@/services/BookingService'

import DatePicker from '@/components/common/DatePicker.vue'

export default {
  name: 'ProductChargesTable',
  components: {
    DatePicker
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    charges: {
      type: Array,
      default: () => []
    },
    groupByBooking: {
      type: Boolean,
      default: false
    },
    bookingGroup: {
      type: Object, // Används bara om groupByBooking är true
      default: null
    },
    booking: {
      type: Object,
      default: null
    },
    bookings: {
      type: Array, // Används bara om groupByBooking är true
      default: () => []
    },
    disableSort: {
      type: Boolean,
      default: false
    },
    showTotalRow: {
      type: Boolean,
      default: false
    },
    summaryToggleEnabled: {
      type: Boolean,
      default: false
    },
    displayedColumns: {
      type: Array,
      default: () => [
        'created_at',
        'product_name',
        'quantity',
        'price',
        'sum',
        'status'
      ]
    }
  },
  data: () => ({
    showProductSummary: false,
    datePickerDialog: {
      show: false,
      chargeItem: null,
      deliveryDate: null
    }
  }),
  computed: {
    isMobile: function () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    deliveryDateSuggestions: function () {
      if (!this.booking && this.bookings.length === 0) {
        return []
      }

      let booking = null
      if (this.booking) {
        booking = this.booking
      } else if (this.datePickerDialog.chargeItem) {
        booking = this.bookings.find(b => b.id === this.datePickerDialog.chargeItem.booking_id)
      }

      if (!booking) {
        return []
      }

      return [
        {
          text: 'Incheckningsdagen',
          date: booking.check_in
        },
        {
          text: 'Utcheckningsdagen',
          date: booking.check_out
        }
      ]
    },
    tableRows: function () {
      const arr = []
      this.charges.forEach(c => {
        arr.push({
          id: c.id,
          key: c.model_name + '_' + c.id,
          modelName: c.model_name,
          booking_id: c.booking_id,
          name: c.product.name,
          note: c.note || c.caption,
          deliveryDate: c.delivery_date,
          deliveryDateFormatted: c.delivery_date ? c.delivery_date.toHumanReadableDate() : null,
          productId: c.product.id,
          discountType: c.discount_type,
          discountValue: c.discount_value,
          fixedPriceDiscountValue: c.discount_type === 'fixed' ? c.original_price - c.price : null,
          fixedPriceDiscountValueFormatted: c.discount_type === 'fixed' ? window.conversion.toHumanReadableAmount((c.original_price - c.price) / 100) : null,
          flatPriceDiscountValue: c.discount_type === 'flat' ? c.discount_value : null,
          flatPriceDiscountValueFormatted: c.discount_type === 'flat' ? window.conversion.toHumanReadableAmount(c.discount_value / 100) : null,
          guiColor: c.product.gui_color,
          quantity: c.quantity / 1000,
          price: c.price / 100,
          priceFormatted: window.conversion.toHumanReadableAmount(c.price / 100),
          originalPrice: c.original_price / 100,
          originalPriceFormatted: window.conversion.toHumanReadableAmount(c.original_price / 100),
          sum: (c.quantity * c.price) / (1000 * 100),
          sumFormatted: window.conversion.toHumanReadableAmount((c.quantity * c.price) / (1000 * 100)),
          createdAt: c.created_at ? c.created_at.toHumanReadableDate() : 'NY',
          isTallyCharge: c.is_tally_charge,
          isBookvisitCharge: c.is_bookvisit_charge,
          status: c.status,
          statusFormatted: c.status === 'invoiced' ? 'Fakturerad' : c.status === 'paid' ? 'Betald' : c.status === 'parked' ? 'I varukorgen' : ''
        })
      })
      return arr
    },
    productSummaryRows: function () {
      const summaryByProductId = {}

      this.tableRows.forEach(row => {
        if (!summaryByProductId[row.productId]) {
          summaryByProductId[row.productId] = {
            key: `product_${row.productId}`,
            productId: row.productId,
            name: row.name,
            guiColor: row.guiColor,
            total: 0,
            paidOrInvoiced: 0,
            inCart: 0
          }
        }

        const productSummary = summaryByProductId[row.productId]
        productSummary.total += row.sum

        if (['paid', 'invoiced'].includes(row.status)) {
          productSummary.paidOrInvoiced += row.sum
        }

        if (row.status === 'parked') {
          productSummary.inCart += row.sum
        }
      })

      return Object.values(summaryByProductId).map(productSummary => {
        return {
          ...productSummary,
          totalFormatted: window.conversion.toHumanReadableAmount(productSummary.total),
          paidOrInvoicedFormatted: window.conversion.toHumanReadableAmount(productSummary.paidOrInvoiced),
          inCartFormatted: window.conversion.toHumanReadableAmount(productSummary.inCart)
        }
      })
    },
    activeTableRows: function () {
      return this.showProductSummary ? this.productSummaryRows : this.tableRows
    },
    shouldGroupByBooking: function () {
      return this.groupByBooking && !this.showProductSummary
    },
    headers: function () {
      const hasSavedCharges = this.charges.filter(c => { return c.id !== 0 }).length > 0

      const headers = []
      if (this.displayedColumns.includes('move_left')) {
        headers.push({ text: 'Flytta', value: 'move_left', align: 'start' })
      }
      if (this.displayedColumns.includes('created_at') && hasSavedCharges) {
        headers.push({ text: 'Sparad', value: 'createdAt', align: 'start' })
      }
      if (this.displayedColumns.includes('product_name')) {
        headers.push({ text: 'Produkt', value: 'name', align: 'start' })
      }
      if (this.displayedColumns.includes('quantity')) {
        headers.push({ text: 'Antal', value: 'quantity', align: 'end' })
      }
      if (this.displayedColumns.includes('price')) {
        headers.push({ text: 'Pris', value: 'price', align: 'end' })
      }
      if (this.displayedColumns.includes('sum')) {
        headers.push({ text: 'Summa', value: 'sumFormatted', align: 'end' })
      }
      if (this.displayedColumns.includes('status')) {
        headers.push({ text: 'Status', value: 'status', align: 'end' })
      }
      if (this.displayedColumns.includes('move_right')) {
        headers.push({ text: 'Flytta', value: 'move_right', align: 'end' })
      }

      return headers
    },
    summaryHeaders: function () {
      return [
        { text: 'Produkt', value: 'name', align: 'start' },
        { text: 'Totalt', value: 'totalFormatted', align: 'end' },
        { text: 'Betalt/fakturerat', value: 'paidOrInvoicedFormatted', align: 'end' },
        { text: 'I varukorgen', value: 'inCartFormatted', align: 'end' }
      ]
    },
    activeHeaders: function () {
      return this.showProductSummary ? this.summaryHeaders : this.headers
    },
    summaryValues: function () {
      if (this.showProductSummary) {
        const summary = this.activeTableRows.reduce((totals, row) => {
          totals.total += row.total || 0
          totals.paidOrInvoiced += row.paidOrInvoiced || 0
          totals.inCart += row.inCart || 0
          return totals
        }, {
          total: 0,
          paidOrInvoiced: 0,
          inCart: 0
        })

        return {
          totalFormatted: window.conversion.toHumanReadableAmount(summary.total),
          paidOrInvoicedFormatted: window.conversion.toHumanReadableAmount(summary.paidOrInvoiced),
          inCartFormatted: window.conversion.toHumanReadableAmount(summary.inCart)
        }
      }

      const summary = this.activeTableRows.reduce((totals, row) => {
        totals.quantity += row.quantity || 0
        totals.sum += row.sum || 0
        return totals
      }, {
        quantity: 0,
        sum: 0
      })

      return {
        quantity: summary.quantity,
        sumFormatted: window.conversion.toHumanReadableAmount(summary.sum)
      }
    }
  },
  methods: {
    moveCharge: function ({ chargeId, direction, quantity }) {
      this.$emit('move-charge', { chargeId, direction, quantity })
    },
    getSummaryCellValue: function (header) {
      return this.summaryValues[header.value] || ''
    },
    getBookingTitle: function (bookingId) {
      if (!bookingId) {
        return 'Avgifter på gruppbokningen'
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return `Bokning #${bookingId}`
      }
      if (!this.bookingGroup?.hasMultipleCustomers) {
        // Behöver inte skriva ut kundnamnet om alla bokningar har samma kund
        return `${booking.booking_number}, ${booking.position.full_name}`
      }
      return `${booking.booking_number}, ${booking.position.full_name}, ${booking.customer.full_name}`
    },
    getBookingIsDeleted: function (bookingId) {
      if (!bookingId) {
        return false
      }
      const booking = this.bookings.find(b => b.id === bookingId)
      if (!booking) {
        return false
      }
      return !!booking.deleted_at
    },
    openDatePicker: function (item) {
      this.datePickerDialog.chargeItem = item
      this.datePickerDialog.deliveryDate = item.deliveryDate
      this.datePickerDialog.show = true
    },
    saveDeliveryDate: function () {
      this.datePickerDialog.show = false

      if (!this.datePickerDialog.chargeItem || !this.datePickerDialog.deliveryDate) {
        console.error('Kunde inte spara leveransdatum: saknas chargeItem eller deliveryDate')
        return false
      }

      const chargeItem = this.datePickerDialog.chargeItem
      let chargeId = null
      let salesItemId = null
      if (chargeItem.modelName === 'Charge') {
        chargeId = chargeItem.id
      } else if (chargeItem.modelName === 'SalesItem') {
        salesItemId = chargeItem.id
      } else {
        console.error('Kunde inte spara leveransdatum: okänt modelName på chargeItem', chargeItem)
        return false
      }

      const deliveryDate = this.datePickerDialog.deliveryDate

      BookingService.updateDeliveryDate({ deliveryDate, chargeId, salesItemId })
        .then(({ data }) => {
          // Snackbars skickas från server
          if (data.status === 'success') {
            // Uppdatera leveransdatum i tabellen direkt
            const charge = this.charges.find(c => c.model_name === chargeItem.modelName && c.id === chargeItem.id)
            if (charge) {
              charge.delivery_date = deliveryDate
            } else {
              console.error('Kunde inte uppdatera leveransdatum i tabellen: kunde inte hitta charge med id', chargeId, 'och modelName', chargeItem.modelName)
            }
          }
        })
    }
  }
}
</script>
